import React from 'react';
import { useNavigate } from 'react-router-dom';

const IRISComponentsTable = () => {
  const navigate = useNavigate();

  const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '32px 24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    backgroundColor: '#ffffff',
    color: '#1a1a1a',
    lineHeight: '1.6',
  };

  const backButtonStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    backgroundColor: 'transparent',
    border: '1px solid #0d47a1',
    borderRadius: '6px',
    color: '#0d47a1',
    fontWeight: '500',
    fontSize: '1rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    marginBottom: '32px',
  };

  const backButtonHoverStyle = {
    backgroundColor: '#e3f2fd',
  };

  const headerStyle = {
    marginBottom: '32px',
  };

  const logoContainer = {
    display: 'flex',
    alignItems: 'baseline',
    gap: '16px',
    marginBottom: '8px',
  };

  const logoStyle = {
    fontSize: '2.6rem',
    fontWeight: '800',
    color: '#f57c00',
    letterSpacing: '-1px',
  };

  const titleStyle = {
    margin: 0,
    fontSize: '2rem',
    fontWeight: '700',
    color: '#0d47a1',
  };

  const subtitleStyle = {
    color: '#555',
    fontSize: '1.1rem',
    marginTop: '4px',
  };

  const tableWrapperStyle = {
    overflowX: 'auto',
    borderRadius: '8px',
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '0.98rem',
  };

  const thStyle = {
    backgroundColor: '#0d47a1',
    color: 'white',
    padding: '16px 20px',
    textAlign: 'left',
    fontWeight: '600',
    borderBottom: '2px solid #003087',
    position: 'sticky',
    top: 0,
    zIndex: 10,
  };

  const tdKeyStyle = {
    backgroundColor: '#fff3e0',
    fontWeight: '600',
    color: '#e65100',
    padding: '16px 20px',
    width: '28%',
    borderBottom: '1px solid #ffe0b2',
    verticalAlign: 'top',
  };

  const tdValueStyle = {
    padding: '16px 20px',
    backgroundColor: '#fafafa',
    borderBottom: '1px solid #e0e0e0',
    verticalAlign: 'top',
  };

  const boldStyle = {
    fontWeight: '600',
    color: '#d81b60',
  };

  const linkStyle = {
    color: '#0d47a1',
    textDecoration: 'none',
    borderBottom: '1px solid #0d47a1',
    transition: 'all 0.2s',
  };

  const componentsData = [
    { name: 'ID', description: 'The unique IRIS ID associated with a specific IRIS metric.' },
    { name: 'Metric Name', description: 'The name of the metric.' },
    {
      name: 'Definition',
      description: (
        <>
          The definition of the metric. Please note that <span style={boldStyle}>bolded words</span> are glossary terms that can be found in the glossary tab.
        </>
      ),
    },
    {
      name: 'Footnote',
      description: (
        <>
          Prompts for information that should be cited in conjunction with the metric. This information enables increased <strong>transparency</strong> in how the reported values were calculated and helps with the dissemination of contextual information that may be crucial to understanding the reported values.
        </>
      ),
    },
    {
      name: 'Calculation',
      description: (
        <>
          An outline of the relevant mathematical calculation, if the metric requires a calculation or formula. Some formulas are based on other IRIS metrics and are clearly indicated as such.
        </>
      ),
    },
    {
      name: 'Usage Guidance',
      description: (
        <>
          Additional information beyond the details and description provided in the metric definition. This may include considerations related to aggregating data for this metric, recommended sources for supplementary information pertaining to the metric, clarification of the metric calculation and intended usage, among other guidance. This is a dynamic field, and the IRIS initiative welcomes feedback on how to further develop this for each metric.
        </>
      ),
    },
    {
      name: 'Primary Impact Category',
      description: (
        <>
          Primary Impact Categories (Column G in IRIS 5.3) delineates the Impact Category that is most relevant to a given metric. For example, the IRIS metric: Land Directly Controlled: Total (OI5408) has Agriculture as its primary Impact Category. This metric has also been tagged to the following IRIS+ Impact Categories: Biodiversity & Ecosystems, Employment, and Land, which may also apply in a specific context.
        </>
      ),
    },
    {
      name: 'Cross-Category',
      description: (
        <>
          Cross-Category filtering (Column H in IRIS 5.3) delineates all IRIS metrics that are not specific to any Impact Categories or Impact Themes. They therefore can apply to any Impact Category or Impact Theme.
        </>
      ),
    },
    {
      name: 'Impact Category & Impact Theme',
      description: (
        <>
          A categorization of each metric across one of the following 16 Impact Categories and corresponding Impact Themes:
          <br /><br />
          <strong>Agriculture</strong> — Smallholder Agriculture, Sustainable Agriculture, Food Security<br />
          <strong>Air</strong> — Clean Air<br />
          <strong>Biodiversity and Ecosystems</strong> — Biodiversity & Ecosystem Conservation<br />
          <strong>Climate</strong> — Climate Change Mitigation, Climate Resilience and Adaptation<br />
          <strong>Diversity and Inclusion</strong> — Gender Lens, Racial Equity<br />
          <strong>Education</strong> — Access to Quality Education<br />
          <strong>Employment</strong> — Quality Jobs<br />
          <strong>Energy</strong> — Energy Access, Clean Energy, Energy Efficiency<br />
          <strong>Financial Services</strong> — Financial Inclusion<br />
          <strong>Health</strong> — Access to Quality Healthcare, Nutrition<br />
          <strong>Infrastructure</strong> — Resilient Infrastructure<br />
          <strong>Land</strong> — Natural Resources Conservation, Sustainable Land Management, Sustainable Forestry<br />
          <strong>Oceans and Coastal Zones</strong> — Marine Resources Conservation & Management<br />
          <strong>Pollution</strong> — Pollution Prevention<br />
          <strong>Real Estate</strong> — Affordable Quality Housing, Green Buildings<br />
          <strong>Waste</strong> — Waste Management<br />
          <strong>Water</strong> — Water, Sanitation, and Hygiene (WASH), Sustainable Water Management
          <br /><br />
          Impact Categories and Impact Themes have been defined and agreed upon together with hundreds of stakeholders around the world and in alignment with existing conventions, frameworks, and taxonomies, including the International Standard Industrial Classification (ISIC). For additional details on the IRIS+ Thematic Taxonomy, refer to the{' '}
          <a href="https://iris.thegiin.org/document/iris-thematic-taxonomy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            IRIS+ Thematic Taxonomy guidance document
          </a>.
        </>
      ),
    },
    {
      name: 'Social/Environmental',
      description: (
        <>
          This component indicates if an IRIS metric is primarily geared for environmentally- or socially- focused activities. This feature makes it easier for users to discern the lens through which an IRIS metric has been published. This component can be useful when working in Impact Themes that yield social and environmental benefits, such as Clean Energy Access.
        </>
      ),
    },
    {
      name: 'Section',
      description: (
        <>
          A primary categorization of each IRIS metric. The IRIS metrics are organized in a framework that includes 5 core sections:
          <br /><br />
          • Organization Description: metrics that focus on the organization's mission, operational model, and location<br />
          • Product Description: metrics that describe the organization's products and services, and target markets<br />
          • Financial Performance: commonly reported financial metrics<br />
          • Operational Impact: metrics that describe the organization's policies, employees, and environmental performance<br />
          • Product Impact: metrics that describe the performance and reach of the organization's products and services
          <br /><br />
          Please note that some metrics apply to more than one section but are placed in only one primary category.
        </>
      ),
    },
    {
      name: 'Subsection',
      description: 'A more detailed categorization of each IRIS metric. There are 21 subsections in total in IRIS 5.3, ranging from Client Information to Environmental Performance.',
    },
    {
      name: 'Citation',
      description: 'The citation that should be used in public materials when providing data related to the specific metric.',
    },
    {
      name: 'Metric Type',
      description: (
        <>
          Specifies whether a particular metric is a 'metric' or 'submetric'. The information captured by a 'submetric' is related to the information captured by its parent 'metric'. For example, Client Individuals: Female (submetric) captures a subset of what is captured under its parent Client Individuals: Total (metric).
          <br /><br />
          Please note that submetrics are not mutually exclusive. For example, individuals reported as part of Client Individuals: Female (submetric) may also be reported as part of Client Individuals: Rural (submetric).
        </>
      ),
    },
    {
      name: 'Related Metrics',
      description: (
        <>
          A list of the IRIS metric and submetrics that relate to the specific metric being reviewed. (See Metric Type for more information).
          <br /><br />
          Please note that submetrics are not mutually exclusive.
        </>
      ),
    },
    {
      name: 'Metric Level',
      description: (
        <>
          Specifies whether the metric captures information at the organization, product/service level or both. IRIS metrics can be used to differentiate between data points that apply to specific products/services offered by the organization and data points that apply to the organization as a whole. In instances where a metric can be used to describe either a product or the organization, organizations are encouraged to indicate the level at which they are reporting information.
        </>
      ),
    },
    {
      name: 'Metric Quantity Type',
      description: (
        <>
          Specifies whether the quantity captured in the metric is a stock or a flow.
          <br />• <strong>Stock</strong>: Metrics that capture information and data at a specified point in time. The majority of stock metrics ask for information reported as of the end of a reporting period.<br />
          • <strong>Flow</strong>: Metrics that capture information and data measured over an interval of time. The majority of flow metrics ask for information based on activities over the course of a reporting period.
          <br /><br />
          As an example, IRIS employee metrics are stock metrics and ask for the number of employees as of the end of the reporting period. IRIS wage metrics are flow metrics and ask for the value of wages paid over the course of the reporting period.
        </>
      ),
    },
    {
      name: 'Reporting Format',
      description: (
        <>
          The format for reporting against the specific metric. Examples of reporting formats include: Unit Referenced Within Metric Definition, Decimal, Text, Unit of Measure (PD1602), amongst others. The reporting format "Unit Referenced Within Metric Definition" reduces confusion and encourages the user to read the metric's definition to understand the unit to be measured and reported, since many of these values are bespoke (e.g., number of full-time employees). Some metrics such as Reporting Currency (OD5990) and Unit of Measure (PD1602) refer to specific sets of option values (which are described within their Definition); for these, users should indicate which option is used.
        </>
      ),
    },
    {
      name: 'SDGs',
      description: (
        <>
          The IRIS metrics that are aligned to the UN Sustainable Development Goals and Targets. For investors wishing to adopt an individualized selection of aligned IRIS metrics to track progress toward self-identified overall impact goals, the IRIS 5.0 Catalog of Metrics has been evaluated against all 169 SDG targets. 78 SDG targets align with the IRIS Catalog of Metrics. Please refer to the{' '}
          <a href="https://iris.thegiin.org/document/iris-and-the-sdgs/" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            IRIS+ and the SDGs guidance paper
          </a> for additional information.
        </>
      ),
    },
    {
      name: 'Dimensions of Impact',
      description: (
        <>
          The IRIS metrics that are aligned to the five dimensions of impact, a leading convention for impact measurement and management. The five dimensions of impact are: WHAT, WHO, HOW MUCH, CONTRIBUTION, and RISK. IRIS metrics have been aligned to the WHAT, WHO, HOW MUCH, and CONTRIBUTION dimensions. This allows investors to more effectively measure and communicate impact performance through an agreed upon convention therefore helping impact investing to expand with integrity while enabling investors and enterprises to drive toward stronger impact results.
        </>
      ),
    },
    {
      name: 'Joint Impact Indicators',
      description: (
        <>
          The IRIS+ metrics that are used as a part of the Joint Impact Indicators (JII), a subset of the HIPSO indicators and the IRIS Catalog of Metrics in topics that are common across investments, including Gender, Jobs and Climate. The new catalog sections provided indicate which metrics align to the JII, which thematic area they align to, and the name(s) and descriptions and calculation guidance for the indicators of which they are a part. This represents a major step forward for harmonized impact measurement and reporting, and will help to reduce the reporting burden on investees and increases availability of comparable impact data to inform decision-making. Please refer to the{' '}
          <a href="https://s3.amazonaws.com/giin-web-assets/iris/assets/files/2021-01-26-IRIS_JII.pdf" target="_blank" rel="noopener noreferrer" style={linkStyle}>
            Joint Impact Indicators guidance paper
          </a> for additional information.
        </>
      ),
    },
    {
      name: 'Stakeholders',
      description: (
        <>
          A person with an interest or concern in something, especially a business. Stakeholders may include customers, clients, users, end users, constituent, and beneficiaries. The IRIS+ System uses the word "Stakeholders" everywhere "Beneficiaries" had previously been found.
        </>
      ),
    },
    {
      name: 'Operational Impact',
      description: 'Indicates whether metrics apply to Environmental Policies & Performance, Social Policies & Performance, Governance & Ownership, in any combination.',
    },
    {
      name: 'Investment Lens',
      description: (
        <>
          A categorization of metrics across several investment lens, cutting across impact categories. Investment lens include:
          <br />• Gender<br />• Geographic Setting<br />• Minority and Disabled<br />• Poverty Level<br />• Small and Medium Enterprises (SME)
        </>
      ),
    },
    {
      name: 'Financials',
      description: 'Metrics to Income Statement, Balance Sheet, or Cash Flow.',
    },
    {
      name: 'Discontinued Metrics',
      description: (
        <>
          These are previous IRIS metrics that have been discontinued due to changes related to the thematic taxonomy and the advent of Core Metrics Sets. Three IRIS metrics have been discontinued for the 5.0 Catalog: Client Locations (PD2587), Sector to Influence (PD8808), and Client Housing Quality Satisfaction (PI8768). While these metrics are marked as discontinued, they are still listed in the catalog of metrics for reference.
        </>
      ),
    },
  ];

  return (
    <div style={containerStyle}>
      {/* Back Button */}
      <button
        onClick={() => navigate('/reporting/iris/iris-giin-compliance-matrix/iris-system-kpi-proj-entities',
          { replace: true }
        )}
        style={backButtonStyle}
        onMouseEnter={(e) => Object.assign(e.currentTarget.style, backButtonHoverStyle)}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        ← Back to IRIS 5.3a Overview
      </button>

      <header style={headerStyle}>
        <div style={logoContainer}>
          <div style={logoStyle}>IRIS+</div>
          <h1 style={titleStyle}>Components of IRIS 5.3a Catalog of Metrics</h1>
        </div>
        <p style={subtitleStyle}>
          Detailed description of each field/column used in the IRIS 5.3a metrics catalog
        </p>
      </header>

      <div style={tableWrapperStyle}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={{ ...thStyle, width: '28%' }}>Component</th>
              <th style={thStyle}>Description</th>
            </tr>
          </thead>
          <tbody>
            {componentsData.map((item, index) => (
              <tr key={index}>
                <td style={tdKeyStyle}>{item.name}</td>
                <td style={tdValueStyle}>{item.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default IRISComponentsTable;